/* eslint-disable react-hooks/exhaustive-deps */
import { useMutation } from "@apollo/client";
import {
  Box,
  CircularProgress,
  Divider,
  Card as ICard,
  Typography,
  CardActionArea,
  useTheme,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import StarSharpIcon from "@mui/icons-material/StarSharp";
import gql from "graphql-tag";
import React, { useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { addFavouriteRestaurant, profile } from "../../../apollo/server";
import ConfigurationContext from "../../../context/Configuration";
import UserContext from "../../../context/User";
import useStyles from "./styles";
import { storeTypeLabel } from "../../../utils/storeType";
import { useTranslation } from "react-i18next";
import { getOpeningStatusWithTime } from "../../../utils/restaurantOpeningStatus";
import { useState } from "react";
import RestaurantClosedModal from "../../RestaurantClosedModal/RestaurantClosedModal";

const ADD_FAVOURITE = gql`
  ${addFavouriteRestaurant}
`;
const PROFILE = gql`
  ${profile}
`;

function PricingDelivery({
  grid,
  minimum,
  deliveryCharges,
  currencySymbol,
  isSmall,
  index,
}) {
  const classes = useStyles();
  const theme = useTheme();
  const containerStyle = !grid
    ? {
        display: "flex",
        alignItems: "center",
        justifyContent: isSmall ? "center" : "flex-start",
      }
    : { display: "block" };
  return (
    <Box style={containerStyle}>
      <Box
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <Typography
          variant="subtitle1"
          color="textSecondary"
          className={classes.priceText}
          style={{
            color: isSmall
              ? index % 2 === 0
                ? theme.palette.primary.main
                : "#5A5858"
              : "#5A5858",
          }}
        >
          {currencySymbol} {minimum}
        </Typography>
        <Typography
          className={classes.priceDescription}
          style={{
            marginLeft: "4px",
            color: isSmall
              ? index % 2 === 0
                ? theme.palette.primary.main
                : "#5A5858"
              : "#5A5858",
          }}
        >
          Minimum
        </Typography>
      </Box>
      {grid && (
        <Divider
          light
          orientation="horizontal"
          style={{ width: "1px", height: "18px", margin: "auto 5px" }}
        />
      )}
      <Box style={{ display: "flex" }}>
        <Typography
          variant="subtitle1"
          color="textSecondary"
          className={classes.priceText}
        >
          {deliveryCharges > 0 ? `${currencySymbol} ${deliveryCharges}` : ``}
        </Typography>
        <Typography
          variant="caption"
          style={{ marginLeft: "4px" }}
          className={classes.priceDescription}
        >
          {deliveryCharges > 0 ? "delivery fee" : ""}
        </Typography>
      </Box>
    </Box>
  );
}
function Card(props) {
  const { t } = useTranslation();
  const item = props.data ?? null;
  const navigateTo = useNavigate();
  const { profile } = useContext(UserContext);
  const theme = useTheme();
  const grid = props.grid ? props.grid : false;
  const heart = profile ? profile.favourite.includes(item?._id) : false;
  const cardImageHeight = props.cardImageHeight
    ? props.cardImageHeight
    : "144px";
  const category = item.categories.map((category) => category.title);
  const configuration = useContext(ConfigurationContext);
  const classes = useStyles(props);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [mutate, { loading }] = useMutation(ADD_FAVOURITE, {
    onCompleted,
    refetchQueries: [{ query: PROFILE }],
  });

  const toggleCloseModal = useCallback(() => {
    setShowCloseModal((prev) => !prev);
  }, []);

  const openingStatus = getOpeningStatusWithTime(props?.data);

  // Separate checks for closed status
  const isClosedDueToTiming = !openingStatus.isOpen;
  const isClosedDueToAvailability = !props?.data?.isAvailable;
  // Coming soon stores are still being set up: listed with a banner and their
  // menu can be browsed, but they take no orders (blocked on the store page).
  const isComingSoon = Boolean(props?.data?.comingSoon);
  const isClosed =
    isComingSoon || isClosedDueToTiming || isClosedDueToAvailability;

  const getOpeningBannerText = useCallback(() => {
    if (isComingSoon) return t("comingSoon");
    if (isClosedDueToAvailability) return t("closed");
    return openingStatus.label ?? t("closed");
  }, [isComingSoon, isClosedDueToAvailability, openingStatus.label, t]);

  function onCompleted() {
    props.showMessage({
      type: "success",
      message: t("favouriteListUpdated"),
    });
  }

  const handleGoToMenu = () => {
    if (props.checkCart(item._id, item.name, item.image, item.slug) && !loading) {
      navigateTo(`/restaurant/${item.slug}`, {
        state: {
          id: item._id,
          name: item.name,
          image: item.image,
          slug: item.slug,
        },
      });
    }
  };

  const handleNavigate = useCallback(() => {
    if (isClosed && !isComingSoon) {
      setShowCloseModal(true);
      return;
    }

    if (props.checkCart(item._id, item.name, item.image, item.slug) && !loading) {
      navigateTo(`/restaurant/${item.slug}`, {
        state: {
          id: item._id,
          name: item.name,
          image: item.image,
          slug: item.slug,
        },
      });
    }
  }, [navigateTo, item, isClosed, isComingSoon, loading]);

  return (
    <>
      <ICard
        className={`${classes.card} ${grid ? classes.gridCard : ""}`}
        style={{
          backgroundColor: props.isSmall
            ? props.index % 2 === 0
              ? "black"
              : theme.palette.primary.main
            : "white",
        }}
      >
        <CardActionArea
          className={grid ? classes.gridAction : undefined}
          onClick={(e) => {
            e.preventDefault();
            handleNavigate()
          }}
        >
          <Box
            className={props.isSmall ? classes.smallWidth : classes.largeWidth}
          >
            <Box
              style={{
                height: cardImageHeight,
                position: "relative",
              }}
              className={classes.imageContainer}
            >
              <Box
                style={{
                  height: cardImageHeight,
                  backgroundImage: `url("${item.image}")`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  width: "100%",
                }}
                className={classes.imgContainer}
              />
              {isClosed && (
                <Box
                  className={classes.closedBanner}
                >
                  <Typography className={classes.closedBannerText}>
                    {getOpeningBannerText()}
                  </Typography>
                </Box>
              )}
              <Box
                className={classes.timeContainer}
                style={{ display: isClosed ? "none" : "block" }}
              >
                <Typography
                  color="textSecondary"
                  className={classes.timeText}
                  style={{ fontSize: ".75rem" }}
                >
                  {item.deliveryTime}
                </Typography>
                <Typography color="textSecondary" className={classes.timeText}>
                  {" "}
                  MIN
                </Typography>
              </Box>
              <Box
                onMouseDown={(e) => e.stopPropagation()}
                size="small"
                disabled={loading}
                style={isClosed ? { top: "62px" } : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!profile) {
                    props.showMessage({
                      type: "error",
                      message: "You are not logged in.",
                    });
                  }
                  if (!loading && profile) {
                    mutate({ variables: { id: item._id } });
                  }
                }}
                className={
                  props.isSmall ? classes.smallHeartBtn : classes.heartBtn
                }
              >
                {loading ? (
                  <CircularProgress size={15} />
                ) : heart ? (
                  <FavoriteIcon fontSize="small" className={classes.icon} />
                ) : (
                  <FavoriteBorderIcon
                    fontSize="small"
                    className={classes.icon}
                  />
                )}
              </Box>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              className={classes.row}
              flexDirection={props.isSmall ? "column" : "row"}
            >
              <Typography
                variant="subtitle1"
                color="textSecondary"
                className={classes.textBold}
                noWrap
                align={props.isSmall ? "center" : "left"}
                style={{
                  color: props.isSmall
                    ? props.index % 2 === 0
                      ? theme.palette.primary.main
                      : "black"
                    : "black",
                }}
              >
                {item.name}
              </Typography>
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: props.isSmall ? "center" : "flex-end",
                }}
              >
                <StarSharpIcon style={{ fontSize: "16px", color: "#448B7B" }} />
                <Typography
                  variant="caption"
                  color="textSecondary"
                  className={`${classes.textBold} ${classes.totalRatingText}`}
                  style={{
                    color: props.isSmall
                      ? props.index % 2 === 0
                        ? "white"
                        : "#5A5858"
                      : "#5A5858",
                  }}
                >
                  {item.reviewData.ratings}
                </Typography>
                <Typography
                  variant="caption"
                  className={classes.totalRatingText}
                  style={{
                    color: props.isSmall
                      ? props.index % 2 === 0
                        ? "white"
                        : "#5A5858"
                      : "#5A5858",
                  }}
                >
                  /5
                </Typography>
                <Typography
                  variant="caption"
                  className={classes.totalRatingText}
                  style={{
                    color: props.isSmall
                      ? props.index % 2 === 0
                        ? "white"
                        : "#5A5858"
                      : "#5A5858",
                    fontSize: "0.875rem",
                    marginLeft: "3px",
                  }}
                >
                  ({item.reviewData.reviews.length})
                </Typography>
              </Box>
            </Box>
            {/* In the grid every card reserves the tagline and store-type
                lines, so a store without them is not shorter than its row */}
            {item.tagline || grid ? (
              <Typography
                variant="caption"
                noWrap
                align={props.isSmall ? "center" : "left"}
                style={{
                  display: "block",
                  padding: "0 10px",
                  color: "#5A5858",
                }}
              >
                {item.tagline || "\u00A0"}
              </Typography>
            ) : null}
            {storeTypeLabel(item) || grid ? (
              <Typography
                variant="caption"
                noWrap
                align={props.isSmall ? "center" : "left"}
                style={{
                  display: "block",
                  padding: "0 10px",
                  fontWeight: 600,
                  color: "#039C14",
                }}
              >
                {storeTypeLabel(item) || "\u00A0"}
              </Typography>
            ) : null}
            <Box
              display="flex"
              justifyContent="space-between"
              className={classes.row}
              flexDirection={props.isSmall ? "column" : "row"}
            >
              <Typography
                display="inline"
                noWrap
                variant="caption"
                className={classes.subDescription}
                align={props.isSmall ? "center" : "left"}
                style={{
                  color: props.isSmall
                    ? props.index % 2 === 0
                      ? theme.palette.primary.main
                      : "#5A5858"
                    : "#5A5858",
                }}
              >
                {category.toString()}
              </Typography>
              <PricingDelivery
                minimum={item.minimumOrder ?? 0}
                deliveryCharges={item.deliveryCharges ?? 0}
                grid={grid}
                currencySymbol={configuration.currencySymbol}
                isSmall={props.isSmall}
                index={props.index}
              />
            </Box>
          </Box>
        </CardActionArea>
      </ICard>
      <RestaurantClosedModal
        isVisible={showCloseModal}
        toggleModal={toggleCloseModal}
        restaurant={item.name}
        handleGoToMenu={handleGoToMenu}
        handleSeeOtherRestaurants={toggleCloseModal}
      />
    </>
  );
}

export default React.memo(Card);
